import { inject, Injectable } from '@angular/core';
import { CurrentStateService, RealtimeActiveService } from '@ddv/behaviors';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { AppWidgetState, DashboardFilter, DashboardSnapshot, DashboardSnapshotModel, Fund, NamedQuery } from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { FundsService, FuzzyDatesService } from '@ddv/reference-data';
import { NewWidgetOnBoard, WidgetOnBoard, WidgetOnBoardService } from '@ddv/widget-on-boards';
import { WidgetExportService } from '@ddv/widgets';
import { combineLatest, firstValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';

import { DashboardExportService } from './dashboard-export.service';
import { DashboardDetails } from '../models/dashboard-details';
import { CreateDashboardRequest, UpdateDashboardInfoRequest, UpdateDashboardRequest } from '../models/dashboard-requests';
import { DashboardWithoutWidgets } from '../models/dashboard-without-widgets';
import { applySettingsOverrides } from '../utils/apply-settings-overrides';

@Injectable()
export class DashboardService {
    private readonly currentStateService = inject(CurrentStateService);
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly fundsService = inject(FundsService);
    private readonly fuzzyDatesService = inject(FuzzyDatesService);
    private readonly namedQueriesService = inject(NamedQueriesService);
    private readonly realtimeActiveService = inject(RealtimeActiveService);
    private readonly dashboardExportService = inject(DashboardExportService);
    private readonly widgetExportService = inject(WidgetExportService);
    private readonly wobService = inject(WidgetOnBoardService);

    private currentDashboard: DashboardDetails | undefined;
    private funds: Fund[] = [];

    constructor() {
        this.currentStateService.isMultiClient$
            .pipe(
                switchMap((isMultiClient) => {
                    return isMultiClient ? of([]) : this.fundsService.funds();
                }))
            .subscribe({
                next: (funds: Fund[]) => this.funds = funds,
            });

        this.widgetExportService.exportRequested
            .subscribe({
                next: ({ datasets, dashboardName, exportWidgetInfos, exportFormat, clientCode }) => {
                    this.dashboardExportService.exportFilteredDashboard(
                        datasets,
                        dashboardName,
                        exportWidgetInfos,
                        exportFormat,
                        clientCode,
                    );
                },
            });
    }

    fetchAllDashboards(clientCode: string): Observable<DashboardSnapshot[]> {
        return this.ddvApiService.invokeServiceWithParams<DashboardSnapshotModel[]>(clientCode, 'dashboards')
            .pipe(map((response) => {
                return response
                    .map((data) => new DashboardSnapshot(data))
                    .sort((a, b) => (a.name?.toLowerCase() ?? '').localeCompare(b.name?.toLowerCase() ?? ''));
            }));
    }

    validateDashboardName(clientCode: string, name: string): Observable<boolean> {
        return this.ddvApiService
            .invokeServiceWithParams<{ valid: boolean }>(clientCode, 'dashboards/is-name-valid', { name: encodeURIComponent(name) })
            .pipe(map((response) => response.valid));
    }

    fetchDashboard(clientCode: string, id: number): Observable<DashboardDetails> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', clientCode)
            .pipe(
                switchMap((useAwsForWidgets) => {
                    if (useAwsForWidgets) {
                        return this.ddvApiService.invokeServiceWithParams<DashboardDetails>(clientCode, `dashboards/${id}`)
                            .pipe(map((dashboard) => {
                                const { widgetsOnBoard, ...dashboardWithoutWidgets } = dashboard;
                                const wobs = widgetsOnBoard ? fromNewWidgetsOnBoard(widgetsOnBoard as unknown as NewWidgetOnBoard[]) : [];
                                return new DashboardDetails(dashboardWithoutWidgets, wobs);
                            }));
                    }

                    return combineLatest([
                        this.ddvApiService.invokeServiceWithParams<DashboardDetails>(clientCode, `dashboards/${id}`),
                        // Remove this after widgets on board migration is complete and the dashboard call returns widgets on board as part of the response
                        this.wobService.getWidgetsOnBoard(clientCode, id),
                    ])
                        .pipe(map(([dashboard, widgetsOnBoard]) => new DashboardDetails({ ...dashboard }, widgetsOnBoard)));
                }),
                tap((dashboard) => this.currentDashboard = dashboard),
                switchMap((dashboard) => {
                    if (dashboard.dashboardPreference) {
                        const { filters, funds } = dashboard.dashboardPreference;
                        if (filters) {
                            dashboard.dashboardPreference.filters = this.mapDashboardFilterValues(filters);
                        }
                        if (this.funds.length && funds) {
                            const fundIds = funds.map((f) => f.fundId) ?? [];
                            dashboard.dashboardPreference.funds = this.funds.filter((fund) => fund.isIncludedInArrayOfIds(fundIds));
                        }
                    }
                    return this.mapNamedQueriesToDashboardWidgets(dashboard, clientCode);
                }),
                tap((dashboard) => {
                    this.realtimeActiveService.updateRealtimeActive(dashboard.widgets);
                    this.fuzzyDatesService.emptyAdHocFuzzyDatesList();
                    this.fuzzyDatesService.emptyInvestorFuzzyDatesList();

                    if (dashboard.isInvestorOnly()) {
                        firstValueFrom(this.updateInvestorFuzzyDates(dashboard.getAllFundCodes(), dashboard.getNamedQueryIds()))
                            .catch(() => {});
                    }

                    if (dashboard.isSingleNamedQuery() && dashboard.isAdhoc()) {
                        const namedQuery = dashboard.widgets[0].namedQuery;
                        if (namedQuery) {
                            firstValueFrom(this.updateAdHocFuzzyDates(namedQuery)).catch(() => {});
                        }
                    }
                }),
            );
    }

    createDashboard(clientCode: string, dashboard: CreateDashboardRequest): Observable<DashboardDetails> {
        return this.ddvApiService
            .invokeServiceWithBody<Partial<DashboardWithoutWidgets>>(clientCode, 'dashboards', 'POST', dashboard)
            .pipe(
                map((response) => new DashboardDetails({ ...response }, [])),
            );
    }

    updateDashboardInfo(clientCode: string, id: number, dashboard: UpdateDashboardInfoRequest): Observable<DashboardDetails> {
        return this.ddvApiService
            .invokeServiceWithBody<Partial<DashboardWithoutWidgets>>(clientCode, `dashboards/${id}/info`, 'PATCH', dashboard)
            .pipe(
                tap((response) => this.updateCurrentDashboardInfo(response)),
                map((response) => new DashboardDetails({ ...response }, [])),
            );
    }

    updateDashboard(clientCode: string, id: number, dashboard: UpdateDashboardRequest): Observable<DashboardDetails> {
        return this.ddvApiService
            .invokeServiceWithBody<Partial<DashboardWithoutWidgets>>(clientCode, `dashboards/${id}`, 'PUT', dashboard)
            .pipe(
                map((response) => new DashboardDetails({ ...response }, [])),
            );
    }

    copyDashboard(clientCode: string, id: number, dashboard: UpdateDashboardInfoRequest): Observable<number> {
        return this.ddvApiService
            .invokeServiceWithBody<{ id: number }>(clientCode, `dashboards/${id}/copy`, 'POST', dashboard)
            .pipe(map((response) => response.id));
    }

    deleteDashboard(clientCode: string, id: number): Observable<void> {
        return this.ddvApiService.invokeServiceWithBody(clientCode, `dashboards/${id}`, 'DELETE', null);
    }

    updateDashboardFilters(clientCode: string, id: number, filters: DashboardFilter[]): Observable<DashboardDetails> {
        return this.ddvApiService
            .invokeServiceWithBody<Partial<DashboardWithoutWidgets>>(clientCode, `dashboards/${id}/filters`, 'PUT', { filters })
            .pipe(
                map((response) => new DashboardDetails({ ...response }, [])),
            );
    }

    openDashboard(clientCode: string, dashboardId: number): Observable<void> {
        return this.ddvApiService.invokeService<void>(clientCode, `user/state/open-dashboards/${dashboardId}`, 'PUT');
    }

    closeDashboard(clientCode: string, dashboardId: number): Observable<void> {
        return this.ddvApiService.invokeService<void>(clientCode, `user/state/open-dashboards/${dashboardId}`, 'DELETE');
    }

    getCurrentDashboard(): DashboardDetails | undefined {
        return this.currentDashboard;
    }

    private mapDashboardFilterValues(filters: DashboardFilter[]): DashboardFilter[] {
        return filters.map((filter) => {
            const { filterValuesType, values } = filter;

            if (filterValuesType === 'number') {
                filter.values = values?.map((value) => value != null ? Number(value) : value);
            }

            if (filterValuesType === 'boolean') {
                filter.values = values?.map((value) => value != null ? value === 'true' : value);
            }

            return filter;
        });
    }

    private mapNamedQueriesToDashboardWidgets(dashboard: DashboardDetails, clientCode?: string): Observable<DashboardDetails> {
        const namedQueries$ = dashboard.widgets.reduce((namedQueries: Observable<NamedQuery | undefined>[], widget) => {
            if (widget.namedQueryId) {
                namedQueries.push(this.namedQueriesService.fetchNamedQuery(widget.namedQueryId, clientCode));
            }

            return namedQueries;
        }, []);

        if (!namedQueries$.length) {
            return of(dashboard);
        }

        return combineLatest(namedQueries$).pipe(
            map((queries) => {
                const namedQueries = queries.filter((namedQuery) => namedQuery !== undefined);

                dashboard.widgets.forEach((widget) => {
                    const namedQuery = namedQueries.find((nq) => widget.namedQueryId === nq?.id);
                    if (namedQuery) {
                        widget.namedQuery = namedQuery;
                    }
                });

                return dashboard;
            }),
        );
    }

    private updateInvestorFuzzyDates(
        funds: { code: string }[],
        namedQueryIds: (string | undefined)[],
    ): Observable<void> {
        return this.fuzzyDatesService.pushMostRecentFuzzyDatesForInvestor(funds, namedQueryIds);
    }

    private updateAdHocFuzzyDates(namedQuery: NamedQuery): Observable<void> {
        return this.fuzzyDatesService.pushMostRecentFuzzyDatesForAdhoc(namedQuery);
    }

    private updateCurrentDashboardInfo(dashboardInfo: Partial<DashboardWithoutWidgets>): void {
        if (!this.currentDashboard) {
            return;
        }

        this.currentDashboard.name = dashboardInfo.name ?? '';
        this.currentDashboard.description = dashboardInfo.description ?? '';
        this.currentDashboard.abbreviation = dashboardInfo.abbreviation ?? '';
        this.currentDashboard.version = dashboardInfo.version ?? 0;
    }
}

// This should be removed after the migration, we switch to the new type of widget on board on the UI
function fromNewWidgetsOnBoard(newWobs: NewWidgetOnBoard[]): WidgetOnBoard[] {
    return newWobs.map((newWob) => {
        const { settingsOverrides, unsubscribedFilters, widget, ...rest } = newWob;

        return new WidgetOnBoard({
            id: rest.id,
            height: rest.height,
            width: rest.width,
            left: rest.leftPosition,
            top: rest.topPosition,
            maximized: rest.isMaximized,
            isMaster: settingsOverrides?.isMasterWidget,
            widgetPosition: {
                startingRow: rest.startRow,
                endRow: rest.endRow,
                startingColumn: rest.startColumn,
                endColumn: rest.endColumn,
                minWidth: 200,
            },
            filters: unsubscribedFilters,
            widget: new AppWidgetState({
                ...widget,
                visualizationConfigs: applySettingsOverrides(widget.visualizationConfigs, settingsOverrides),
                name: widget.name,
                customDisplayName: settingsOverrides?.customName ?? widget.customDisplayName,
                displayNameType: settingsOverrides?.displayNameType ?? widget.displayNameType,
                isMaster: settingsOverrides?.isMasterWidget ?? widget.isMaster,
                isSubscribedToDashboardFilters: settingsOverrides?.isSubscribedToDashboardFilters ??
                    widget.widgetSettings?.isSubscribedToDashboardFilters,
                sortTableBy: settingsOverrides?.defaultSortOnForDefaultVisualization ?? widget.sortTableBy,
                tableSortDirection: settingsOverrides?.defaultSortDirectionForDefaultVisualization ?? widget.tableSortDirection,
                realtimeUpdates: settingsOverrides?.realtimeUpdates ?? widget.realtimeUpdates,
            }),
        });
    });
}
